import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  Clock,
  Crosshair,
  Move,
  Package,
  Pause,
  Play,
  RefreshCw,
  Truck,
  Warehouse,
  X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;
const WAREHOUSE = { latitude: -12.000868639438853, longitude: -77.05378452821896 };

const TIME_WINDOWS = {
  1: '1:00 PM - 4:00 PM',
  2: '2:00 PM - 5:00 PM',
  3: '3:00 PM - 6:00 PM',
  4: '4:00 PM - 7:00 PM',
  5: '5:00 PM - 8:00 PM',
  6: '6:00 PM - 9:00 PM',
};

const COLORS = ['#B1336E', '#67A43C', '#E6B43C', '#2563EB', '#F97316', '#7C3AED', '#0F766E', '#DC2626'];
const TERMINAL_STATUSES = new Set(['entregado', 'rechazado', 'cancelado']);

function todayInputValue() {
  return new Date().toISOString().slice(0, 10);
}

function timeWindow(slot) {
  return TIME_WINDOWS[Number(slot)] || null;
}

function compactTimeWindow(slot) {
  const label = timeWindow(slot);
  return label ? label.replace(/:00/g, '').replace(/\sPM/g, 'PM') : '';
}

function driverKey(driverId) {
  return driverId == null ? 'unassigned' : String(driverId);
}

function statusLabel(status) {
  const labels = {
    pending: 'Pendiente',
    encamino: 'En camino',
    entregado: 'Entregado',
    rechazado: 'Rechazado',
    cancelado: 'Cancelado',
    postergado: 'Postergado',
  };
  return labels[status] || status || 'Sin estado';
}

function formatTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' });
}

function routeRisk(delivery) {
  if (!delivery.slot) return false;
  if (!delivery.sequence) return true;
  const seq = Number(delivery.sequence);
  const slot = Number(delivery.slot);
  return (slot <= 2 && seq > 5) || (slot === 3 && seq > 8);
}

function DriverRouteMonitor({ setActiveComponent }) {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);
  const routesLayerRef = useRef(null);
  const overviewFitRequestedRef = useRef(true);

  const [selectedDate, setSelectedDate] = useState(todayInputValue());
  const [serviceMinutes, setServiceMinutes] = useState(7);
  const [readyPackagesOnly, setReadyPackagesOnly] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [expandedDrivers, setExpandedDrivers] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [bulkSelection, setBulkSelection] = useState(null);
  const [rectangleMode, setRectangleMode] = useState(false);
  const [selectionBox, setSelectionBox] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyDriver, setBusyDriver] = useState(null);
  const [message, setMessage] = useState('');
  const [draggedDeliveryId, setDraggedDeliveryId] = useState(null);

  const driverColor = useMemo(() => {
    const map = {};
    drivers.forEach((driver, index) => {
      map[driverKey(driver.user_id)] = COLORS[index % COLORS.length];
    });
    map.unassigned = '#64748B';
    return map;
  }, [drivers]);

  const visibleDeliveries = useMemo(() => {
    if (selectedDrivers.length === 0) return deliveries;
    const allowed = new Set(selectedDrivers.map(String));
    return deliveries.filter((delivery) => allowed.has(driverKey(delivery.driver_id)));
  }, [deliveries, selectedDrivers]);

  const deliveriesByDriver = useMemo(() => {
    const grouped = {};
    deliveries.forEach((delivery) => {
      const key = driverKey(delivery.driver_id);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(delivery);
    });
    Object.values(grouped).forEach((items) => {
      items.sort((a, b) => (a.sequence || 999999) - (b.sequence || 999999) || a.id - b.id);
    });
    return grouped;
  }, [deliveries]);

  const summary = useMemo(() => ({
    total: deliveries.length,
    withWindow: deliveries.filter((delivery) => delivery.slot).length,
    noSequence: deliveries.filter((delivery) => !delivery.sequence).length,
    review: deliveries.filter(routeRisk).length,
  }), [deliveries]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const overviewParams = { date: selectedDate };
      if (readyPackagesOnly) {
        overviewParams.package_status = 'in_fastmax';
      }

      const [overviewRes, driversRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/driver-route-monitor`, { params: overviewParams }),
        axios.get(`${API_URL}/api/drivers`),
      ]);
      setDeliveries(overviewRes.data.deliveries || []);
      setDrivers(overviewRes.data.drivers || []);
      setAllDrivers(driversRes.data || []);
      setMessage('');
    } catch (error) {
      console.error('Error loading route monitor:', error);
      setMessage(error.response?.data?.error || 'No se pudo cargar la ruta de conductores');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, readyPackagesOnly]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!mapNodeRef.current || mapRef.current) return;

    const map = L.map(mapNodeRef.current, { zoomControl: false }).setView([WAREHOUSE.latitude, WAREHOUSE.longitude], 12);
    mapRef.current = map;
    markersLayerRef.current = L.layerGroup().addTo(map);
    routesLayerRef.current = L.layerGroup().addTo(map);

    L.control.zoom({ position: 'bottomright' }).addTo(map);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    return () => {
      if (map.dragging) map.dragging.enable();
      if (map.doubleClickZoom) map.doubleClickZoom.enable();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !markersLayerRef.current || !routesLayerRef.current) return;

    markersLayerRef.current.clearLayers();
    routesLayerRef.current.clearLayers();

    const bounds = [];
    visibleDeliveries.forEach((delivery) => {
      if (!delivery.latitude || !delivery.longitude) return;
      const color = driverColor[driverKey(delivery.driver_id)] || '#64748B';
      const tw = compactTimeWindow(delivery.slot);
      const risk = routeRisk(delivery);

      const icon = L.divIcon({
        className: '',
        html: `
          <div style="position: relative; display: inline-flex; flex-direction: column; align-items: center;">
            <div style="
              min-width: 28px;
              height: 28px;
              border-radius: 999px;
              background: ${color};
              color: white;
              border: 2px solid white;
              box-shadow: 0 2px 6px rgba(15,23,42,.35);
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 11px;
              font-weight: 800;
              padding: 0 5px;
            ">${delivery.sequence || '-'}</div>
            ${tw ? `<div style="
              margin-top: 1px;
              border-radius: 999px;
              border: 1px solid ${risk ? '#FCA5A5' : '#CBD5E1'};
              background: ${risk ? '#FEE2E2' : 'white'};
              color: ${risk ? '#B91C1C' : '#334155'};
              font-size: 9px;
              font-weight: 800;
              padding: 1px 5px;
              white-space: nowrap;
              box-shadow: 0 1px 3px rgba(15,23,42,.2);
            ">${tw}</div>` : ''}
          </div>
        `,
        iconSize: [70, tw ? 42 : 28],
        iconAnchor: [14, 14],
      });

      const marker = L.marker([delivery.latitude, delivery.longitude], { icon })
        .on('click', () => setSelectedDelivery(delivery))
        .addTo(markersLayerRef.current);
      marker.bindTooltip(`#${delivery.sequence || '-'} ${delivery.recipient_name || ''}`, { direction: 'top' });
      bounds.push([delivery.latitude, delivery.longitude]);
    });

    const selected = selectedDrivers.length ? selectedDrivers : drivers.map((driver) => driver.user_id);
    selected.forEach((driverId) => {
      const points = (deliveriesByDriver[driverKey(driverId)] || [])
        .filter((delivery) => delivery.latitude && delivery.longitude)
        .sort((a, b) => (a.sequence || 999999) - (b.sequence || 999999))
        .map((delivery) => [delivery.latitude, delivery.longitude]);
      if (points.length > 1) {
        L.polyline(points, {
          color: driverColor[driverKey(driverId)] || '#64748B',
          weight: 3,
          opacity: 0.65,
        }).addTo(routesLayerRef.current);
      }
    });

    if (bounds.length && overviewFitRequestedRef.current) {
      mapRef.current.fitBounds(L.latLngBounds(bounds), { padding: [60, 60], maxZoom: 14 });
      overviewFitRequestedRef.current = false;
    }
  }, [visibleDeliveries, driverColor, selectedDrivers, drivers, deliveriesByDriver]);

  useEffect(() => {
    if (!rectangleMode) {
      setSelectionBox(null);
    }
  }, [rectangleMode]);

  function getMapPoint(event) {
    const rect = mapNodeRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  function handleSelectionStart(event) {
    if (!rectangleMode) return;
    event.preventDefault();
    const point = getMapPoint(event);
    if (!point) return;
    setSelectionBox({
      startX: point.x,
      startY: point.y,
      endX: point.x,
      endY: point.y,
    });
  }

  function handleSelectionMove(event) {
    if (!rectangleMode || !selectionBox) return;
    event.preventDefault();
    const point = getMapPoint(event);
    if (!point) return;
    setSelectionBox((current) => current ? { ...current, endX: point.x, endY: point.y } : current);
  }

  function handleSelectionEnd(event) {
    if (!rectangleMode || !selectionBox || !mapRef.current) return;
    event.preventDefault();
    const point = getMapPoint(event);
    const finalBox = point ? { ...selectionBox, endX: point.x, endY: point.y } : selectionBox;
    const minX = Math.min(finalBox.startX, finalBox.endX);
    const maxX = Math.max(finalBox.startX, finalBox.endX);
    const minY = Math.min(finalBox.startY, finalBox.endY);
    const maxY = Math.max(finalBox.startY, finalBox.endY);

    if (Math.abs(maxX - minX) < 8 || Math.abs(maxY - minY) < 8) {
      setSelectionBox(null);
      setRectangleMode(false);
      return;
    }

    const northWest = mapRef.current.containerPointToLatLng(L.point(minX, minY));
    const southEast = mapRef.current.containerPointToLatLng(L.point(maxX, maxY));
    const bounds = L.latLngBounds(northWest, southEast);
    const selected = visibleDeliveries.filter((delivery) => (
      delivery.latitude && delivery.longitude && bounds.contains([delivery.latitude, delivery.longitude])
    ));

    setSelectionBox(null);
    setRectangleMode(false);
    if (selected.length === 0) {
      setMessage('No hay entregas dentro del area seleccionada.');
      return;
    }
    setBulkSelection(selected);
  }

  function toggleDriver(driverId) {
    const key = String(driverId);
    setSelectedDrivers((prev) => (
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key]
    ));
  }

  function toggleExpanded(driverId) {
    const key = String(driverId);
    setExpandedDrivers((prev) => (
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key]
    ));
  }

  async function updateDelivery(deliveryId, patch) {
    await axios.patch(`${API_URL}/api/admin/driver-route-monitor/delivery-point/${deliveryId}`, patch);
    await loadData();
  }

  async function resequenceDriver(driverId, orderedIds) {
    await axios.patch(`${API_URL}/api/admin/driver-route-monitor/resequence`, {
      driver_id: driverId,
      delivery_point_ids: orderedIds,
    });
    await loadData();
  }

  function applyLocalSequence(driverId, orderedIds) {
    const driver = driverKey(driverId);
    const sequenceById = new Map(orderedIds.map((id, index) => [Number(id), index + 1]));

    setDeliveries((currentDeliveries) => currentDeliveries.map((delivery) => {
      if (driverKey(delivery.driver_id) !== driver || !sequenceById.has(Number(delivery.id))) {
        return delivery;
      }

      return {
        ...delivery,
        sequence: sequenceById.get(Number(delivery.id)),
      };
    }));
  }

  function handleDrop(driverId, targetDeliveryId) {
    if (!draggedDeliveryId || draggedDeliveryId === targetDeliveryId) return;
    const current = deliveriesByDriver[driverKey(driverId)] || [];
    const moving = current.find((delivery) => delivery.id === draggedDeliveryId);
    if (!moving) return;

    const withoutMoving = current.filter((delivery) => delivery.id !== draggedDeliveryId);
    const targetIndex = withoutMoving.findIndex((delivery) => delivery.id === targetDeliveryId);
    withoutMoving.splice(targetIndex < 0 ? withoutMoving.length : targetIndex, 0, moving);
    const orderedIds = withoutMoving.map((delivery) => delivery.id);

    applyLocalSequence(driverId, orderedIds);
    resequenceDriver(driverId, orderedIds).catch((error) => {
      console.error('Error resequencing driver route:', error);
      setMessage(error.response?.data?.error || 'No se pudo actualizar la secuencia');
      loadData();
    });
    setDraggedDeliveryId(null);
  }

  async function playDriver(driverId) {
    setBusyDriver(driverId);
    try {
      await axios.post(`${API_URL}/api/admin/driver-route-monitor/drivers/${driverId}/play`, {
        date: selectedDate,
        service_minutes_per_stop: serviceMinutes,
      });
      setMessage('Ruta iniciada y horas estimadas actualizadas sin cambiar secuencias.');
      await loadData();
    } catch (error) {
      setMessage(error.response?.data?.error || 'No se pudo iniciar la ruta');
    } finally {
      setBusyDriver(null);
    }
  }

  async function pauseDriver(driverId) {
    setBusyDriver(driverId);
    try {
      await axios.post(`${API_URL}/api/admin/driver-route-monitor/drivers/${driverId}/pause`, { date: selectedDate });
      setMessage('Ruta pausada. Las entregas en camino volvieron a pendiente.');
      await loadData();
    } catch (error) {
      setMessage(error.response?.data?.error || 'No se pudo pausar la ruta');
    } finally {
      setBusyDriver(null);
    }
  }

  async function setWarehouseLocation(driverId) {
    setBusyDriver(driverId);
    try {
      await axios.post(`${API_URL}/api/admin/driver-route-monitor/drivers/${driverId}/warehouse-location`);
      setMessage('Ubicacion del conductor movida al almacen.');
      await loadData();
    } catch (error) {
      setMessage(error.response?.data?.error || 'No se pudo mover al almacen');
    } finally {
      setBusyDriver(null);
    }
  }

  async function moveDriverToDelivery(delivery) {
    if (!delivery?.driver_id || !delivery.latitude || !delivery.longitude) return;
    try {
      await axios.post(`${API_URL}/api/admin/driver-route-monitor/drivers/${delivery.driver_id}/location`, {
        latitude: delivery.latitude,
        longitude: delivery.longitude,
      });
      setMessage('Ubicacion del conductor movida a este punto.');
      setSelectedDelivery(null);
      await loadData();
    } catch (error) {
      setMessage(error.response?.data?.error || 'No se pudo mover el conductor');
    }
  }

  const selectedDeliveryIds = bulkSelection?.map((delivery) => delivery.id) || [];

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-slate-900">
      <div ref={mapNodeRef} className="absolute inset-0" />

      {rectangleMode && (
        <div
          className="absolute inset-0 z-[820] cursor-crosshair"
          onMouseDown={handleSelectionStart}
          onMouseMove={handleSelectionMove}
          onMouseUp={handleSelectionEnd}
        >
          {selectionBox && (
            <div
              className="absolute border-2 border-[#B1336E] bg-[#B1336E]/10 shadow-lg"
              style={{
                left: Math.min(selectionBox.startX, selectionBox.endX),
                top: Math.min(selectionBox.startY, selectionBox.endY),
                width: Math.abs(selectionBox.endX - selectionBox.startX),
                height: Math.abs(selectionBox.endY - selectionBox.startY),
              }}
            />
          )}
          <div className="absolute left-[396px] top-16 rounded-lg bg-white/95 px-3 py-2 text-xs font-bold text-slate-700 shadow-lg">
            Arrastra sobre el mapa para seleccionar entregas
          </div>
        </div>
      )}

      <aside className="absolute left-0 top-0 bottom-0 z-[900] w-[380px] max-w-[88vw] bg-white/95 backdrop-blur border-r border-slate-200 shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <h1 className="text-lg font-extrabold text-[#B1336E] font-montserrat">Rutas de Conductores</h1>
            <p className="text-xs text-slate-500 font-montserrat">
              {readyPackagesOnly ? 'Paquetes en FastMax listos para asignar' : 'Secuencias creadas por driver/admin'}
            </p>
          </div>
          <button onClick={() => setActiveComponent('OptionsAdmin')} className="p-2 rounded-lg hover:bg-slate-100">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="grid grid-cols-4 gap-2 p-3 border-b border-slate-200 text-center">
          <Metric label="Total" value={summary.total} />
          <Metric label="Horario" value={summary.withWindow} />
          <Metric label="Sin orden" value={summary.noSequence} />
          <Metric label="Revisar" value={summary.review} tone={summary.review ? 'danger' : 'ok'} />
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {drivers.map((driver) => (
            <DriverCard
              key={driver.user_id}
              driver={driver}
              color={driverColor[driverKey(driver.user_id)]}
              deliveries={deliveriesByDriver[driverKey(driver.user_id)] || []}
              selected={selectedDrivers.includes(String(driver.user_id))}
              expanded={expandedDrivers.includes(String(driver.user_id))}
              busy={busyDriver === driver.user_id}
              onToggle={() => toggleDriver(driver.user_id)}
              onExpand={() => toggleExpanded(driver.user_id)}
              onPlay={() => playDriver(driver.user_id)}
              onPause={() => pauseDriver(driver.user_id)}
              onWarehouse={() => setWarehouseLocation(driver.user_id)}
              onDragStart={setDraggedDeliveryId}
              onDrop={handleDrop}
              onSelectDelivery={setSelectedDelivery}
            />
          ))}
          {drivers.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
              {readyPackagesOnly
                ? 'No hay conductores con paquetes en FastMax para esta fecha.'
                : 'No hay conductores con entregas para esta fecha.'}
            </div>
          )}
        </div>
      </aside>

      <div className="absolute left-[396px] top-4 right-4 z-[850] flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 rounded-lg bg-white shadow-lg border border-slate-200 px-3 py-2">
          <Calendar className="w-4 h-4 text-[#B1336E]" />
          <input
            type="date"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
            className="text-sm font-semibold text-slate-700 outline-none"
          />
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-white shadow-lg border border-slate-200 px-3 py-2">
          <Clock className="w-4 h-4 text-[#67A43C]" />
          <input
            type="number"
            min="0"
            max="60"
            value={serviceMinutes}
            onChange={(event) => setServiceMinutes(event.target.value)}
            className="w-14 text-sm font-semibold text-slate-700 outline-none"
          />
          <span className="text-xs font-semibold text-slate-500">min por entrega</span>
        </div>
        <button
          onClick={() => {
            overviewFitRequestedRef.current = true;
            loadData();
          }}
          className="inline-flex items-center gap-2 rounded-lg bg-white shadow-lg border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refrescar
        </button>
        <button
          onClick={() => {
            overviewFitRequestedRef.current = true;
            setReadyPackagesOnly((value) => !value);
          }}
          className={`inline-flex items-center gap-2 rounded-lg shadow-lg border px-3 py-2 text-sm font-bold ${
            readyPackagesOnly ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-200'
          }`}
        >
          <Package className="w-4 h-4" />
          En FastMax
        </button>
        <button
          onClick={() => setRectangleMode((value) => !value)}
          className={`inline-flex items-center gap-2 rounded-lg shadow-lg border px-3 py-2 text-sm font-bold ${
            rectangleMode ? 'bg-[#B1336E] text-white border-[#B1336E]' : 'bg-white text-slate-700 border-slate-200'
          }`}
        >
          <Crosshair className="w-4 h-4" />
          Seleccionar area
        </button>
      </div>

      {message && (
        <div className="absolute left-[396px] bottom-4 z-[850] max-w-xl rounded-lg bg-white border border-slate-200 shadow-lg px-4 py-3 text-sm font-semibold text-slate-700">
          {message}
        </div>
      )}

      {selectedDelivery && (
        <DeliveryModal
          delivery={selectedDelivery}
          drivers={allDrivers}
          onClose={() => setSelectedDelivery(null)}
          onUpdate={updateDelivery}
          onMoveDriver={moveDriverToDelivery}
        />
      )}

      {bulkSelection && (
        <BulkAssignModal
          selectedDeliveries={bulkSelection}
          drivers={allDrivers}
          onClose={() => setBulkSelection(null)}
          onConfirm={async (driverId) => {
            await axios.patch(`${API_URL}/api/admin/driver-route-monitor/bulk-assign`, {
              delivery_point_ids: selectedDeliveryIds,
              driver_id: driverId,
              append_sequence: true,
            });
            setBulkSelection(null);
            await loadData();
          }}
        />
      )}
    </div>
  );
}

function Metric({ label, value, tone }) {
  const color = tone === 'danger' ? 'text-red-700' : tone === 'ok' ? 'text-green-700' : 'text-slate-900';
  return (
    <div className="rounded-lg bg-slate-50 px-2 py-2">
      <p className={`text-lg font-extrabold font-montserrat ${color}`}>{value}</p>
      <p className="text-[10px] text-slate-500 font-montserrat">{label}</p>
    </div>
  );
}

function DriverCard({
  driver,
  color,
  deliveries,
  selected,
  expanded,
  busy,
  onToggle,
  onExpand,
  onPlay,
  onPause,
  onWarehouse,
  onDragStart,
  onDrop,
  onSelectDelivery,
}) {
  const listRef = useRef(null);
  const scrollFrameRef = useRef(null);
  const scrollDirectionRef = useRef(0);

  const stats = {
    encamino: deliveries.filter((delivery) => delivery.status === 'encamino').length,
    pending: deliveries.filter((delivery) => delivery.status === 'pending').length,
    completed: deliveries.filter((delivery) => TERMINAL_STATUSES.has(delivery.status)).length,
    review: deliveries.filter(routeRisk).length,
  };

  const cancelAutoScroll = useCallback(() => {
    scrollDirectionRef.current = 0;
    if (scrollFrameRef.current) {
      cancelAnimationFrame(scrollFrameRef.current);
      scrollFrameRef.current = null;
    }
  }, []);

  const stepAutoScroll = useCallback(() => {
    const list = listRef.current;
    const direction = scrollDirectionRef.current;

    if (!list || !direction) {
      scrollFrameRef.current = null;
      return;
    }

    list.scrollTop += direction;
    scrollFrameRef.current = requestAnimationFrame(stepAutoScroll);
  }, []);

  const handleListDragOver = useCallback(
    (event) => {
      event.preventDefault();

      const list = listRef.current;
      if (!list) return;

      const rect = list.getBoundingClientRect();
      const edgeSize = 64;
      const maxSpeed = 18;
      let direction = 0;

      if (event.clientY < rect.top + edgeSize) {
        const intensity = 1 - Math.max(0, event.clientY - rect.top) / edgeSize;
        direction = -Math.max(4, Math.round(maxSpeed * intensity));
      } else if (event.clientY > rect.bottom - edgeSize) {
        const intensity = 1 - Math.max(0, rect.bottom - event.clientY) / edgeSize;
        direction = Math.max(4, Math.round(maxSpeed * intensity));
      }

      scrollDirectionRef.current = direction;

      if (direction && !scrollFrameRef.current) {
        scrollFrameRef.current = requestAnimationFrame(stepAutoScroll);
      }

      if (!direction) {
        cancelAutoScroll();
      }
    },
    [cancelAutoScroll, stepAutoScroll]
  );

  useEffect(() => cancelAutoScroll, [cancelAutoScroll]);

  return (
    <div className={`rounded-xl border bg-white shadow-sm ${selected ? 'border-[#B1336E]' : 'border-slate-200'}`}>
      <div className="p-3">
        <div className="flex items-start gap-2">
          <button onClick={onToggle} className="mt-1 h-4 w-4 rounded-full border-2" style={{ backgroundColor: selected ? color : 'white', borderColor: color }} />
          <button onClick={onExpand} className="flex-1 text-left min-w-0">
            <div className="flex items-center gap-2">
              <Truck className="w-4 h-4" style={{ color }} />
              <h3 className="font-bold text-sm text-slate-900 font-montserrat truncate">{driver.name || `Conductor ${driver.user_id}`}</h3>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{deliveries.length} entregas · {stats.pending} pendientes · {stats.encamino} en camino</p>
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-3">
          <IconButton label="Play" Icon={Play} onClick={onPlay} disabled={busy} color="bg-[#67A43C] text-white" />
          <IconButton label="Pausa" Icon={Pause} onClick={onPause} disabled={busy} color="bg-amber-500 text-white" />
          <IconButton label="Almacen" Icon={Warehouse} onClick={onWarehouse} disabled={busy} color="bg-slate-800 text-white" />
        </div>

        {stats.review > 0 && (
          <div className="mt-2 flex items-center gap-1 rounded-lg bg-red-50 px-2 py-1 text-xs font-semibold text-red-700">
            <AlertTriangle className="w-3 h-3" />
            {stats.review} entregas con horario para revisar
          </div>
        )}
      </div>

      {expanded && (
        <div
          ref={listRef}
          onDragOver={handleListDragOver}
          onDragLeave={cancelAutoScroll}
          className="max-h-80 overflow-y-auto border-t border-slate-100 p-2 space-y-1"
        >
          {deliveries.map((delivery) => (
            <button
              key={delivery.id}
              draggable
              onDragStart={() => onDragStart(delivery.id)}
              onDragEnd={cancelAutoScroll}
              onDrop={() => {
                cancelAutoScroll();
                onDrop(driver.user_id, delivery.id);
              }}
              onClick={() => onSelectDelivery(delivery)}
              className="w-full rounded-lg border border-slate-100 bg-slate-50 px-2 py-2 text-left hover:border-[#B1336E]/30"
            >
              <div className="flex items-start gap-2">
                <span className="flex h-7 min-w-7 items-center justify-center rounded-full bg-white text-xs font-extrabold text-slate-700 border">
                  {delivery.sequence || '-'}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-bold text-slate-900">{delivery.recipient_name || 'Sin nombre'}</p>
                  <p className="truncate text-[11px] text-slate-500">{delivery.district || delivery.address || '-'}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{statusLabel(delivery.status)}</span>
                    {delivery.slot && <span className="rounded-full bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-700">{compactTimeWindow(delivery.slot)}</span>}
                    {delivery.arrival_time && <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold text-blue-700">ETA {formatTime(delivery.arrival_time)}</span>}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function IconButton({ label, Icon, onClick, disabled, color }) {
  return (
    <button onClick={onClick} disabled={disabled} className={`flex items-center justify-center gap-1 rounded-lg px-2 py-2 text-xs font-bold disabled:opacity-50 ${color}`}>
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

function DeliveryModal({ delivery, drivers, onClose, onUpdate, onMoveDriver }) {
  const [driverId, setDriverId] = useState(delivery.driver_id || '');
  const [sequence, setSequence] = useState(delivery.sequence || '');
  const tw = timeWindow(delivery.slot);

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <div>
            <h2 className="font-extrabold text-[#B1336E] font-montserrat">Entrega #{delivery.id}</h2>
            <p className="text-xs text-slate-500">{delivery.recipient_name}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <InfoRow label="Direccion" value={`${delivery.address || '-'} ${delivery.district ? `· ${delivery.district}` : ''}`} />
          <InfoRow label="Estado" value={statusLabel(delivery.status)} />
          <InfoRow label="Ventana horaria" value={tw || 'Sin ventana'} />
          <InfoRow label="ETA" value={formatTime(delivery.arrival_time)} />

          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-bold text-slate-600">
              Conductor
              <select value={driverId} onChange={(event) => setDriverId(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                <option value="">Sin conductor</option>
                {drivers.map((driver) => (
                  <option key={driver.user_id || driver.id} value={driver.user_id || driver.id}>{driver.name || driver.email}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-bold text-slate-600">
              Secuencia
              <input value={sequence} onChange={(event) => setSequence(event.target.value)} type="number" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-2">
            <button
              onClick={() => onUpdate(delivery.id, { driver_id: driverId ? Number(driverId) : null, sequence: sequence ? Number(sequence) : null }).then(onClose)}
              className="rounded-lg bg-[#B1336E] px-4 py-2 text-sm font-bold text-white"
            >
              Guardar
            </button>
            <button
              onClick={() => onMoveDriver(delivery)}
              disabled={!delivery.driver_id || !delivery.latitude || !delivery.longitude}
              className="flex items-center justify-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
            >
              <Move className="w-4 h-4" />
              Mover conductor aqui
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <p className="text-[11px] font-bold uppercase text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-slate-900">{value}</p>
    </div>
  );
}

function BulkAssignModal({ selectedDeliveries, drivers, onClose, onConfirm }) {
  const [driverId, setDriverId] = useState('');
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <h2 className="font-extrabold text-[#B1336E]">Asignar seleccion</h2>
          <button onClick={onClose} className="rounded-lg p-2 hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-slate-600">{selectedDeliveries.length} entregas seleccionadas dentro del area.</p>
          <select value={driverId} onChange={(event) => setDriverId(event.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="">Selecciona conductor</option>
            {drivers.map((driver) => (
              <option key={driver.user_id || driver.id} value={driver.user_id || driver.id}>{driver.name || driver.email}</option>
            ))}
          </select>
          <button disabled={!driverId} onClick={() => onConfirm(Number(driverId))} className="w-full rounded-lg bg-[#B1336E] px-4 py-3 text-sm font-bold text-white disabled:opacity-50">
            Asignar y agregar al final de su secuencia
          </button>
        </div>
      </div>
    </div>
  );
}

export default DriverRouteMonitor;
